import React, {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { InfoIcon, LockOpenIcon, TriangleAlertIcon } from "@lucide/icons";

import { roleInfo } from "simplydo/core/permissions";
import type { OpenAPI } from "simplydo/interfaces";
import {
  Accordion,
  AccordionContent,
  AccordionTitle,
} from "simplydo/react/ui/accordion";
import { Checkbox } from "simplydo/react/ui/checkbox";
import { Divider } from "simplydo/react/ui/divider";
import { H5 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Image } from "simplydo/react/ui/image";
import { Input } from "simplydo/react/ui/input";
import { Label } from "simplydo/react/ui/label";
import {
  Message,
  MessageContent,
  MessageHeader,
} from "simplydo/react/ui/message";
import { Popup } from "simplydo/react/ui/popup";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "simplydo/react/ui/table";

import api from "api";
import util from "utils/utils";

import ConfigurableTable from "components/lib/ConfigurableTable";

import type { PermissionCategory, PermissionGroup } from ".";
import type { RolePermission, RoleWithInformation, RolesUser } from "../types";

type RoleEditorPermissionsProps = {
  removePermissionsFromRole: (roleId: string, permissions: string[]) => void;
  addPermissionsToRole: (roleId: string, permissions: string[]) => void;
  forType: OpenAPI.Schemas["Role"]["forType"];
  permissionGroups: PermissionGroup[];
  forId: string;
  usingRole: OpenAPI.Schemas["Role"] & RoleWithInformation;
  setRoles: Dispatch<
    SetStateAction<Array<OpenAPI.Schemas["Role"] & RoleWithInformation>>
  >;
};

type PermissionTableProps = {
  groupId: string;
  permissionCategories: PermissionCategory[];
  usingRole: OpenAPI.Schemas["Role"] & RoleWithInformation;
  toggleIndividualPermission: (permission: string) => void;
};

export const PermissionTable = ({
  groupId,
  permissionCategories,
  usingRole,
  toggleIndividualPermission,
}: PermissionTableProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const lowerSearchQuery = searchQuery.toLowerCase();

  const { t } = useTranslation();
  const selectedPermissionSet: Set<string> = new Set(
    usingRole?.permissions ?? [],
  );

  const permissionByKey = new Map(
    permissionCategories
      .flatMap((category) => category.permissions)
      .map((permission) => {
        const parentKey = permission.key.replace(`-${groupId}`, "");
        return [parentKey, permission];
      }),
  );

  const getPermissionTitle = (permissionKey: string) =>
    permissionByKey.get(permissionKey)?.title ?? permissionKey;

  return (
    <>
      <Input
        placeholder="Search for permissions..."
        value={searchQuery}
        onChange={(e, { value }) => setSearchQuery(value)}

        style={{ marginBottom: 12 }}
      />
      <Table style={{ marginTop: 0 }}>
        {permissionCategories.map((category, i) => {
          const filteredPermissions = category.permissions.filter(
            (perm) =>
              t(perm.title).toLowerCase().includes(lowerSearchQuery) ||
              t(perm.text).toLowerCase().includes(lowerSearchQuery),
          );
          if (filteredPermissions.length === 0) return null;

          return (
            <React.Fragment key={category.name}>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell
                    colSpan={2}
                    style={{
                      padding: "7px 12px",
                      borderTop:
                        i !== 0 ? "1px solid rgba(34, 36, 38, .1)" : "none",
                    }}
                  >
                    {t("common:capitalise", { key: category.name })}
                  </TableHeaderCell>
                  <TableHeaderCell
                    style={{
                      padding: "7px 12px",
                      borderTop:
                        i !== 0 ? "1px solid rgba(34, 36, 38, .1)" : "none",
                    }}
                  ></TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPermissions.map((permission) => {
                  const permissionIsSelected = selectedPermissionSet.has(
                    permission.key,
                  );
                  const missingDependencies = (
                    permission.dependsOn ?? []
                  ).filter((requiredPermission) => {
                    const splitKey = requiredPermission.key.split(".");
                    const scopedPermissionKey =
                      splitKey[0] +
                      "-" +
                      groupId +
                      "." +
                      splitKey.slice(1).join(".");
                    return (
                      !selectedPermissionSet.has(requiredPermission.key) &&
                      !selectedPermissionSet.has(scopedPermissionKey)
                    );
                  });
                  const hasMissingSelectedDependencies =
                    permissionIsSelected && missingDependencies.length > 0;

                  return (
                    <TableRow key={permission.key}>
                      <TableCell collapsing>
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <Checkbox
                            disabled={usingRole?.preventUserEditing}
                            toggle
                            id={`permission-${permission.key}`}
                            checked={permissionIsSelected}
                            onChange={() =>
                              toggleIndividualPermission(permission.key)
                            }
                          />
                        </div>
                      </TableCell>
                      <TableCell collapsing>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                          }}
                        >
                          <label htmlFor={`permission-${permission.key}`}>
                            <span style={{ fontWeight: 600, marginRight: 16 }}>
                              {t(permission.title)}
                            </span>
                          </label>
                          {hasMissingSelectedDependencies ? (
                            <Popup
                              trigger={
                                <Label size="small" color="orange">
                                  {missingDependencies.length > 1
                                    ? "Missing dependencies"
                                    : "Missing dependency"}
                                </Label>
                              }
                              content={
                                <div>
                                  <b>
                                    Users will be restricted when using this
                                    permission by itself.
                                  </b>
                                  <p>
                                    If you are sure, you can save this role
                                    without the missing{" "}
                                    {util.pluralise(
                                      missingDependencies.length,
                                      "dependency",
                                      "dependencies",
                                      false,
                                    )}
                                    . Users won&apos;t be able to fully utilise
                                    this role unless another role grants them
                                    the required{" "}
                                    {util.pluralise(
                                      missingDependencies.length,
                                      "permission",
                                      "permissions",
                                      false,
                                    )}
                                    .
                                  </p>
                                </div>
                              }
                            />
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell>
                        {t(permission.text)}
                        {permission.dependsOn?.length ? (
                          <div
                            style={{
                              marginTop: 2,
                              gap: 4,
                              display: "flex",
                              flexDirection: "row",
                              fontSize: 12,
                              flexWrap: "wrap",
                            }}
                          >
                            <Icon
                              icon={
                                hasMissingSelectedDependencies
                                  ? TriangleAlertIcon
                                  : LockOpenIcon
                              }
                              color={
                                hasMissingSelectedDependencies
                                  ? "orange"
                                  : undefined
                              }
                              style={{ marginRight: 0 }}
                            />
                            Depends on:
                            <span>
                              {permission.dependsOn.map(
                                (requiredPermission, index) => {
                                  const splitKey =
                                    requiredPermission.key.split(".");
                                  const scopedPermissionKey =
                                    splitKey[0] +
                                    "-" +
                                    groupId +
                                    "." +
                                    splitKey.slice(1).join(".");

                                  const isMissing =
                                    permissionIsSelected &&
                                    !selectedPermissionSet.has(
                                      requiredPermission.key,
                                    ) &&
                                    !selectedPermissionSet.has(
                                      scopedPermissionKey,
                                    );

                                  return (
                                    <React.Fragment
                                      key={`${permission.key}-${requiredPermission.key}`}
                                    >
                                      <Popup
                                        trigger={
                                          <span
                                            style={{
                                              textDecoration:
                                                requiredPermission.reason
                                                  ? "underline dotted"
                                                  : "none",
                                              color: isMissing
                                                ? "#9f3a38"
                                                : "inherit",
                                            }}
                                          >
                                            <strong>
                                              {t(
                                                getPermissionTitle(
                                                  requiredPermission.key,
                                                ),
                                              )}
                                            </strong>
                                            {requiredPermission.type ===
                                            "recommended"
                                              ? " (rec.)"
                                              : ""}
                                          </span>
                                        }
                                        disabled={!requiredPermission.reason}
                                        content={t(
                                          requiredPermission.reason ?? "",
                                        )}
                                      />
                                      {index <
                                      (permission.dependsOn?.length ?? 0) - 1
                                        ? ", "
                                        : ""}
                                    </React.Fragment>
                                  );
                                },
                              )}
                            </span>
                          </div>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </React.Fragment>
          );
        })}
      </Table>
    </>
  );
};

const RoleEditorPermissions = ({
  addPermissionsToRole,
  removePermissionsFromRole,
  forType,
  forId,
  permissionGroups,
  setRoles,
  usingRole,
}: RoleEditorPermissionsProps) => {
  const { t } = useTranslation();
  const usingForId = forType === "global" ? "global" : forId;

  // Global roles can specify the organisations they're being used for, when the role is just specifically about ghosting
  const showOrgSelector =
    forType === "global" && !usingRole?.preventUserEditing;
  const [orgState, setOrgState] = useState<{
    loading: boolean;
    organisations: OpenAPI.Schemas["Organisation"][];
    page: number;
    query: string;
    total: number;
    nextPageAvailable: boolean;
    previousPageAvailable: boolean;
  }>({
    loading: false,
    organisations: [],
    page: 1,
    query: "",
    total: 0,
    nextPageAvailable: false,
    previousPageAvailable: false,
  });

  const [activePermissionGroup, setActivePermissionGroup] = useState<
    string | null
  >(permissionGroups[0]?.id ?? null);

  // Functions for handling org selection
  const fetchOrganisations = useCallback(() => {
    if (showOrgSelector) {
      setOrgState((prevOrgState) => ({ ...prevOrgState, loading: true }));
      api.organisations.getAll(
        { page: orgState.page, query: orgState.query },
        (data) => {
          setOrgState((prevOrgState) => ({
            ...prevOrgState,
            loading: false,
            organisations: data.organisations,
            total: data.total,
            nextPageAvailable: data.nextPageAvailable,
            previousPageAvailable: data.previousPageAvailable,
          }));
        },
        () => {
          setOrgState((prevOrgState) => ({ ...prevOrgState, loading: false }));
        },
      );
    }
  }, [orgState.page, orgState.query, showOrgSelector]);

  useEffect(() => {
    fetchOrganisations();
  }, [fetchOrganisations]);

  const onSelectOrg = (organisation: OpenAPI.Schemas["Organisation"]) => {
    const organisationId = organisation._id;
    const currentRoleBeingEdited = usingRole;
    if (!currentRoleBeingEdited) return;
    const currentRoleGlobalForOrgIds = [
      ...(currentRoleBeingEdited?.scopes?.organisations ?? []),
    ];
    const currentRoleGlobalForOrgs = [
      ...(currentRoleBeingEdited?.organisations ?? []),
    ];
    const orgIsSelected = currentRoleGlobalForOrgIds.includes(organisationId);
    let updatedGlobalForOrgIds: string[] = [];
    let updatedGlobalForOrgs: { _id?: string; name?: string; code?: string }[] =
      [];
    if (orgIsSelected) {
      updatedGlobalForOrgIds = currentRoleGlobalForOrgIds.filter(
        (orgId) => orgId !== organisationId,
      );
      updatedGlobalForOrgs = currentRoleGlobalForOrgs.filter(
        (org) => org._id !== organisationId,
      );
    } else {
      updatedGlobalForOrgIds = [...currentRoleGlobalForOrgIds, organisationId];
      updatedGlobalForOrgs = [...currentRoleGlobalForOrgs, organisation];
    }
    setRoles((prevRoles) =>
      prevRoles.map((role) =>
        role._id === currentRoleBeingEdited._id
          ? {
              ...role,
              scopes: {
                ...(role?.scopes ?? {}),
                organisations: updatedGlobalForOrgIds,
              },
              organisations: updatedGlobalForOrgs,
            }
          : role,
      ),
    );
    api.roles.updateRole(
      forType,
      usingForId,
      currentRoleBeingEdited._id,
      { scopes: { organisations: updatedGlobalForOrgIds } },
      () => {},
      (err) => {
        toast.error(err.message);
        setRoles((prevRoles) =>
          prevRoles.map((role) =>
            role._id === currentRoleBeingEdited._id
              ? {
                  ...role,
                  scopes: {
                    ...(role?.scopes ?? {}),
                    organisations: currentRoleGlobalForOrgIds,
                  },
                  organisations: currentRoleGlobalForOrgs,
                }
              : role,
          ),
        );
      },
    );
  };

  const getPermissionDetail = (permissionKey: string) => {
    for (const group of permissionGroups) {
      const found = group.permissions
        .flatMap((category) => category.permissions)
        .find((perm) => perm.key === permissionKey);
      if (found) return found;
    }
    return undefined;
  };

  const removeSinglePermissionFromRole = (
    roleId: string,
    permission: OpenAPI.Schemas["Role"]["permissions"][number],
  ) => {
    const permissionDetail = getPermissionDetail(permission);
    util
      .confirm(
        `Removing "${t(permissionDetail?.title || permission)}" from "${usingRole.name}"`,
        "Are you sure you want to remove this permission from this role? You are able to add it back at any time.",
      )
      .then(() => {
        removePermissionsFromRole(roleId, [permission]);
      })
      .catch(() => {});
  };

  const toggleIndividualPermission = (permission: string) => {
    if (!usingRole) return;
    if (usingRole.permissions.includes(permission as any)) {
      removeSinglePermissionFromRole(usingRole._id, permission as any);
    } else {
      addPermissionsToRole(usingRole._id, [permission]);
    }
  };

  return (
    <>
      {usingRole?.preventUserEditing ? (
        <div>
          <Message color="blue">
            <Icon icon={InfoIcon} />
            <MessageHeader>System role information</MessageHeader>
            <MessageContent>
              This role is a system role, its permissions cannot be edited. It
              is required for the system to function correctly.
            </MessageContent>
          </Message>
          <Divider hidden />
        </div>
      ) : null}
      <Accordion styled fluid>
        {permissionGroups.map((group) => (
          <>
            <AccordionTitle
              key={group.id}
              onClick={() =>
                setActivePermissionGroup(
                  activePermissionGroup === group.id ? null : group.id,
                )
              }
              active={activePermissionGroup === group.id}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div style={{ display: "flex", gap: 4 }}>
                  <Icon icon={roleInfo[group.type].icon} />
                  {group.title}
                </div>
                <Label size="small">
                  {
                    usingRole.permissions.filter((p: RolePermission) =>
                      group.permissions.some((category) =>
                        category.permissions.some((perm) => perm.key === p),
                      ),
                    ).length
                  }
                  /
                  {group.permissions.reduce(
                    (acc, category) => acc + category.permissions.length,
                    0,
                  )}{" "}
                  assigned
                </Label>
              </div>
            </AccordionTitle>
            <AccordionContent
              key={group.id + "-content"}
              active={activePermissionGroup === group.id}
            >
              <PermissionTable
                groupId={group.id}
                permissionCategories={group.permissions}
                usingRole={usingRole}
                toggleIndividualPermission={toggleIndividualPermission}
              />
            </AccordionContent>
          </>
        ))}
      </Accordion>

      {showOrgSelector ? (
        <div>
          <Divider hidden />
          <H5>Limit to specific organisations</H5>
          <p>
            Choose organisations that these permissions should be restricted to.
            Leave these empty to allow unrestricted superadmin access.
          </p>

          <Input
            placeholder="Search for organisations by name/code.."
            value={orgState.query}
            onChange={(e) =>
              setOrgState((prevOrgState) => ({
                ...prevOrgState,
                query: e.target.value,
              }))
            }
            fluid
            style={{ marginBottom: 12 }}
          />

          <ConfigurableTable
            onSelect={(org) => {
              if (org) {
                onSelectOrg(org);
              }
            }}
            selectedKeys={usingRole?.scopes?.organisations ?? []}
            tableKey="superadmin-organisations"
            data={orgState.organisations}
            keyExtractor={(org) => org._id}
            preventSelectAll
            columns={[
              {
                key: "darkLogoUrl",
                name: "",
                settingName: "Image",
                center: true,
                width: 40,
                render: ({ cell }) => (
                  <Image
                    style={{ objectFit: "contain" }}
                    avatar
                    src={typeof cell === "string" ? cell : undefined}
                  />
                ),
              },
              { key: "name", name: `Organisation (${orgState.total})` },
              {
                key: "code",
                name: "Code",
                render: ({ cell }) => (
                  <span
                    style={{
                      background: "#efefef",
                      fontSize: "11px",
                      display: "flex",
                      justifyContent: "center",
                      alignContent: "center",
                      borderRadius: 3,
                      padding: "0 3px",
                    }}
                  >
                    {String(cell ?? "")}
                  </span>
                ),
              },
            ]}
          />
        </div>
      ) : null}
    </>
  );
};

export default RoleEditorPermissions;
